import { useEffect, useRef } from 'react'
import mapboxgl, { Map, MapMouseEvent, Marker } from 'mapbox-gl'
import type { ActiveTool, GeoreferenceValue } from './georeference-step'

export const POINT_ZOOM_LEVEL = 14

type OnPointSelect = (lng: number, lat: number, searchLabel: string | null) => void

// Places a point on click in Pin mode, and keeps the marker + camera in sync with form state.
export function useMarkerSync(
  map: Map | null,
  activeTool: ActiveTool,
  georeference: GeoreferenceValue,
  onPointSelect: OnPointSelect,
) {
  const markerRef = useRef<Marker | null>(null)

  // Only in Pin mode — otherwise a draw-tool click would also drop a marker.
  useEffect(() => {
    if (!map || activeTool !== 'pin') return
    const handleClick = (e: MapMouseEvent) => onPointSelect(e.lngLat.lng, e.lngLat.lat, null)
    map.on('click', handleClick)
    return () => {
      map.off('click', handleClick)
    }
  }, [map, activeTool, onPointSelect])

  useEffect(() => {
    if (!map) return
    if (
      activeTool === 'pin' &&
      georeference.type === 'POINT' &&
      georeference.geometry?.type === 'Point'
    ) {
      const [lng, lat] = georeference.geometry.coordinates
      if (!markerRef.current) {
        markerRef.current = new mapboxgl.Marker().setLngLat([lng, lat]).addTo(map)
      } else {
        markerRef.current.setLngLat([lng, lat])
      }
      map.flyTo({ center: [lng, lat], zoom: POINT_ZOOM_LEVEL })
    } else {
      markerRef.current?.remove()
      markerRef.current = null
    }
  }, [map, activeTool, georeference])
}
